import { useState, useMemo, useCallback, useEffect, useContext } from "react";
import { useAssortment } from "../model/useAssortment";

import { Header } from "../components/SharedComponents/sharedComponents.index";

import { currencyFormatter, createPercentFormatter, textFormatter } from "../utils/formatter/formatter";
import { TenantContext } from "../templates/context/TenantContext";

import { ASSORTMENT_INDUSTRY_TABS } from "../components/components.constants";

const RecomendacaoBadge = ({ value }) => {
  const STYLES = {
    'Prioridade A': { color: '#16a34a', bg: '#dcfce7', border: '#bbf7d0' },
    'Prioridade B': { color: '#16a34a', bg: '#dcfce7', border: '#bbf7d0' },
    'Monitorar':    { color: '#b45309', bg: '#fef3c7', border: '#fde68a' },
    'Cortar':       { color: '#dc2626', bg: '#fee2e2', border: '#fecaca' },
  };

  if (!value) return null;

  const style = STYLES[value] || { color: '#555', bg: '#f5f5f5', border: '#e5e5e5' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '6px',
      fontSize: '12px', fontWeight: '700',
      backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}`,
      whiteSpace: 'nowrap',
    }}>
      {value}
    </span>
  );
};

const pctColor = ({ percentValue, mixShareValuePercent }) => {
  if (Number(percentValue) >= Number(mixShareValuePercent)) 
    return { color: "#16a34a", bg: "#dcfce7" };
  return { color: "#dc2626", bg: "#fee2e2" };
};

const buildFirstStepOptionsByView = ({
  levels, 
  labels, 
  activeFilters, 
  seasonId, 
  labelToLevelKey,
  upperLevelsHierarchy = ['level1', 'level2', 'level3', 'level4'],
  lowerLevelsHierarchy = ['level5', 'level6', 'level7', 'level8'],
  }) => {
  const buildOptions = (currentKey) => {
    const relevantLevels = levels.filter(item => {
      if (seasonId && item.seasonId !== seasonId) return false;
      return activeFilters.every(([filterLabel, filterVal]) => {
        const levelKey = labelToLevelKey[filterLabel];
        if (!levelKey) return true;
        if (levelKey === currentKey) return true;
        const itemVal = item.levels?.[levelKey];
        const vals = Array.isArray(filterVal) ? filterVal : [filterVal];
        return vals.includes(itemVal);
      });
    });

    const grouped = new Map();
    relevantLevels.forEach(item => {
      const value = item.levels?.[currentKey];
      if (!value) return;
      if (!grouped.has(value)) grouped.set(value, { label: value, count: 0, items: [] });
      grouped.get(value).count += 1;
      grouped.get(value).items.push(item);
    });

    return Array.from(grouped.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  };

  return {
    hierarquia: Object.fromEntries(
      upperLevelsHierarchy.map(key => [textFormatter(labels[key]), buildOptions(key)])
    ),
    atributos: Object.fromEntries(
      lowerLevelsHierarchy.map(key => [textFormatter(labels[key]), buildOptions(key)])
    ),
  };
};

const ppFmtPct = (val) => {
  if (val == null) return '—';
  const pct = val > 1 ? val : val * 100;

  return `${pct.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const ppGetPctColor = (val) => {
  if (val == null) return '#aaa';
  const v = val > 1 ? val / 100 : val;
  if (v >= 0.5) return '#16a34a';
  if (v >= 0.2) return '#d97706';
  return '#dc2626';
};

export const useAssortmentViewModel = ({ 
  activeTab,
  shouldFetchFinancialSummry,
  shouldFetchProductPerformanceSummary,
  shouldFetchCollectionPrediction,
  shouldFetchHistoricalMatrix
 } = {}) => {

  const [historicalFilters, setHistoricalFilters] = useState({});
  const [selectedType, setSelectedType] = useState(null);

  const [estimatesFilters, setEstimatesFilters] = useState({});

  const [detailedMatrixSeason, setDetailedMatrixSeason] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [onlyWithSimilars, setOnlyWithSimilars] = useState(false);

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandsInitialized, setBrandsInitialized] = useState(false);

  const [financialSeason, setFinancialSeason] = useState(null);

  const [userSelectedTypeId, setUserSelectedTypeId] = useState(null);
  const [userSelectedProductId, setUserSelectedProductId] = useState(null);

  const [productReportSeason, setProductReportSeason] = useState(null);
  const [ppCodeFilter, setPpCodeFilter] = useState('');
  const [ppAdvancedFilters, setPpAdvancedFilters] = useState({});
  const [ppExpandedProductId, setPpExpandedProductId] = useState(null);
  const [pdfExportProductIds, setPdfExportProductIds] = useState(null);
  const [pdfExportSeasonId, setPdfExportSeasonId] = useState(null);

  const { hierarchy } = useContext(TenantContext)

  const { 
    productImage: { 
      data: productImageData, 
      isLoading: isLoadingProductImage 
    },

    financialSummary: {
      data: financialSummaryData,
      isLoading: isLoadingFinancialSummaryData,
    },

    productPerformanceSummary: {
      data: productPerformanceSummaryData,
      isLoading: isLoadingProductPerformanceSummaryData,
    },

    collectionPrediction: {
      data: collectionPredictionData,
      isLoading: isLoadingCollectionPredictionData,
    },
    
    historicalMatrix: {
      data: historicalMatrixData,
      isLoading: isLoadingHistoricalMatrixData,
    },

    productTag: {
      data: productTagData,
      isLoading: isLoadingProductTag
    },

    productImgForPDFGeneration: {
      data: pdfExportImagesData,
      isLoading: isLoadingPdfExportImages
    }
  } = useAssortment({ 
    shouldFetchFinancialSummry,
    shouldFetchProductPerformanceSummary,
    shouldFetchCollectionPrediction,
    shouldFetchHistoricalMatrix,
    selectedProductId: selectedProduct?.productId || null,
    selectedTagProductId: ppExpandedProductId || null,
    seasonId: productReportSeason,
    productIdsForPDFGeneration: pdfExportProductIds,
    pdfExportSeasonId: pdfExportSeasonId
  });

  const typesFromPerformanceAggregationMatrix = useMemo(() => 
    collectionPredictionData?.types || [],
  [collectionPredictionData]);

  const typesFromProductReport = useMemo(() => {
    const list = productPerformanceSummaryData?.productReport ?? [];
    const seen = new Map();

    list.forEach((item) => {
      const l = item?.levels ?? {};
      const typeKey = `${item?.season?.seasonId}__${item?.productId}_${l.level4}__${l.level5}__${l.level6}__${l.level7}_${l.level8}`;
      if (seen.has(typeKey)) return;

      seen.set(typeKey, {
        name: l.level4,
        path: [l.level5, l.level6, l.level7].filter(Boolean).join(' · '),
        score: item?.score,
        productId: item?.productId,
        seasonId: item?.season?.seasonId,
        seasonName: item?.season?.seasonName,
        isCurrentSellingSeason: item?.season?.isCurrentSellingSeason,
        levels: l,
      });
    });

    return Array.from(seen.values());
  }, [productPerformanceSummaryData]);

  /**
   * Lógica para a aba de dados financeiros (análise shistóricas)
   */
  const financialTabColumns = useMemo(() => [
    {
      id: 'level1',
      accessorKey: 'level1',
      header: <Header ctx={{ title: [textFormatter('marca / tipo', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ row, getValue }) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {row.getCanExpand() && (
            <span style={{ color: '#aaa', fontSize: '12px' }}>
              {row.getIsExpanded() ? '▾' : '›'}
            </span>
          )}
          <span style={{ fontWeight: row.depth === 0 ? '700' : '400' }}>
            {getValue()}
            {row.depth === 0 && row.original.items?.length > 0 && (
              <span style={{ fontWeight: '400', color: '#bbb', fontSize: '12px', marginLeft: 4 }}>
                ({row.original.items.length} tipos)
              </span>
            )}
          </span>
        </span>
      ),
    },
    {
      id: 'mixQty',
      accessorKey: 'mixQty',
      header: <Header ctx={{ title: [textFormatter('mix', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue }) => <strong>{getValue()}</strong>,
    },
    {
      id: 'mixShare',
      accessorKey: 'mixShare',
      header: <Header ctx={{ title: [textFormatter('mix %', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue }) => `${createPercentFormatter(
        {
          minimumFractionDigits: 0, 
          maximumFractionDigits: 0 
        }).format(getValue())}`,
    },
    {
      id: 'revenue',
      accessorKey: 'revenue',
      header: <Header ctx={{ title: [textFormatter('faturamento', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue }) => currencyFormatter.format(getValue()),
    },
    {
      id: 'profit',
      accessorKey: 'profit',
      header: <Header ctx={{ title: [textFormatter('lucro real', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue }) => currencyFormatter.format(getValue()),
    },
    {
      id: 'unitsSold',
      accessorKey: 'unitsSold',
      header: <Header ctx={{ title: [textFormatter('pçs vendidas', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue }) => getValue()?.toLocaleString('pt-BR'),
    },
    {
      id: 'revenueShare',
      accessorKey: 'revenueShare',
      header: <Header ctx={{ title: [textFormatter('% fat.', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue, row }) => {
        const val = getValue();
        const { color, bg } = pctColor({ percentValue: val, mixShareValuePercent: row?.original?.mixShare });
        return (
          <span style={{ color, backgroundColor: bg, padding: '2px 8px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
            {createPercentFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)}
          </span>
        );
      },
    },
    {
      id: 'profitShare',
      accessorKey: 'profitShare',
      header: <Header ctx={{ title: [textFormatter('% lucro', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue, row }) => {
        const val = getValue();
        const { color, bg } = pctColor({ percentValue: val, mixShareValuePercent: row?.original?.mixShare });
        return (
          <span style={{ color, backgroundColor: bg, padding: '2px 8px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
            {createPercentFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)}
          </span>
        );
      },
    },
    {
      id: 'quantitySalesShare',
      accessorKey: 'quantitySalesShare',
      header: <Header ctx={{ title: [textFormatter('% qtd', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue, row }) => {
        const val = getValue();
        const { color, bg } = pctColor({ percentValue: val, mixShareValuePercent: row?.original?.mixShare });
        return (
          <span style={{ color, backgroundColor: bg, padding: '2px 8px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
            {createPercentFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)}
          </span>
        );
      },
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: <Header ctx={{ title: [textFormatter('recomendação', { textTransform: 'uppercase' })], tooltip: 'INSERIR' }} />,
      cell: ({ getValue }) => <RecomendacaoBadge value={getValue()} />,
    },
  ], []);

  const financialBrands = useMemo(() => {
    const data = financialSummaryData?.assortmentFinancialMetrics ?? [];
    const filtered = financialSeason 
      ? data.filter(item => item.seasonId === financialSeason) 
      : data;
    const unique = [...new Set(filtered.map(item => item.level1))];
    return unique.map(name => ({ name }));
  }, [financialSummaryData, financialSeason]);

  const visibleBrands = useMemo(() => {
    let data = financialSummaryData?.assortmentFinancialMetrics ?? [];

    if (financialSeason) data = data.filter(item => item.seasonId === financialSeason);

    if (selectedBrands.length > 0) data = data.filter(item => selectedBrands.includes(item.level1));
    else return [];
    
    return data;
  }, [selectedBrands, financialSeason, financialSummaryData]);

  const financialTotals = useMemo(() => {
    return visibleBrands.reduce((acc, brand) => {
      const brandRevenue = brand.items.reduce((s, i) => s + (i.revenue ?? 0), 0);
      const brandProfit = brand.items.reduce((s, i) => s + (i.profit ?? 0), 0);
      const brandMix = brand.items.reduce((s, i) => s + (i.mixQty ?? 0), 0);
      const brandUnits = brand.items.reduce((s, i) => s + (i.unitsSold ?? 0), 0);
      return {
        mix: acc.mix + brandMix,
        fat: acc.fat + brandRevenue,
        lucro: acc.lucro + brandProfit,
        pcs: acc.pcs + brandUnits,
      };
    }, { mix: 0, fat: 0, lucro: 0, pcs: 0 });
  }, [visibleBrands]);

  const financialSeasons = useMemo(() =>
    financialSummaryData?.seasons ?? [],
    [financialSummaryData]
  );  

  const financialSeasonOptions = useMemo(() =>
    financialSeasons.map(season => ({ 
      value: season.seasonId, 
      label: season.seasonName,
      isCurrentSellingSeason: season.isCurrentSellingSeason
    })),
    [financialSeasons]
  );

  const visibleBrandsWithShares = useMemo(() => {
    const totalMix = financialTotals.mix || 1;
    const totalRevenue = financialTotals.fat || 1;
    const totalProfit = financialTotals.lucro || 1;
    const totalUnits = financialTotals.pcs || 1;

    return visibleBrands.map(brand => {
      const brandMix = brand.items.reduce((acc, i) => acc + (i.mixQty ?? 0), 0);
      const brandRevenue = brand.items.reduce((acc, i) => acc + (i.revenue ?? 0), 0);
      const brandProfit = brand.items.reduce((acc, i) => acc + (i.profit ?? 0), 0);
      const brandUnits = brand.items.reduce((acc, i) => acc + (i.unitsSold ?? 0), 0);

      return {
        ...brand,
        mixQty: brandMix,
        revenue: brandRevenue,
        profit: brandProfit,
        unitsSold: brandUnits,
        // marca: % relativo ao total geral
        mixShare: brandMix / totalMix,
        revenueShare: brandRevenue / totalRevenue,
        profitShare: brandProfit / totalProfit,
        quantitySalesShare: brandUnits / totalUnits,
        items: brand.items.map(item => ({
          ...item,
          // itens: % relativo ao total da marca
          mixShare: item.mixQty / (brandMix || 1),
          revenueShare: item.revenue / (brandRevenue || 1),
          profitShare: item.profit / (brandProfit || 1),
          quantitySalesShare: item.unitsSold / (brandUnits || 1),
        }))
        .sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0)),
      };
    }).sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0));;
  }, [visibleBrands, financialTotals]);

  useEffect(() => {
    if (!financialSeasons.length) return;

    const newestCurrentSelling = [...financialSeasons]
      .sort((a, b) => b.seasonId - a.seasonId)
      .find((season) => season.isCurrentSellingSeason);

    setFinancialSeason(newestCurrentSelling?.seasonId ?? financialSeasons[0]?.seasonId ?? null);
  }, [financialSeasons]);

  useEffect(() => {
    if (financialBrands.length === 0) return;

    if (!brandsInitialized) {
      setSelectedBrands(financialBrands.map(b => b.name));
      setBrandsInitialized(true);
      return;
    }

    const validNames = new Set(financialBrands.map(b => b.name));
    setSelectedBrands(prev => prev.filter(name => validNames.has(name)));
  }, [financialBrands, brandsInitialized]);

  /**
   * Lógica para a aba de matriz detalhada (análises históricas)
   */

  const labelToLevelKey = useMemo(() => {
    const labels = hierarchy?.labels ?? {};
    return Object.entries(labels).reduce((acc, [levelKey, label]) => {
      acc[textFormatter(label)] = levelKey;
      return acc;
    }, {});
  }, [hierarchy]);

  const filterOptions = useMemo(() => buildFirstStepOptionsByView({
    levels: historicalMatrixData?.aggregationMetrics || [],
    labels: hierarchy?.labels ?? {},
    activeFilters: Object.entries(historicalFilters).filter(([_, val]) => val && (Array.isArray(val) ? val.length > 0 : true)),
    seasonId: detailedMatrixSeason,
    labelToLevelKey: labelToLevelKey,
    lowerLevelsHierarchy: ['level5', 'level6', 'level7'],
  }), [historicalMatrixData, hierarchy, historicalFilters, detailedMatrixSeason, labelToLevelKey]);

  const filteredTypes = useMemo(() => {
    let all = historicalMatrixData?.aggregationMetrics ?? [];

    if (detailedMatrixSeason) all = all.filter(item => item.seasonId === detailedMatrixSeason);

    const activeFilters = Object.entries(historicalFilters).filter(([, val]) => {
      if (!val) return false;
      if (Array.isArray(val)) return val.length > 0;
      return true;
    });

    if (activeFilters.length === 0) return all.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR'));

    const filtered = all.filter(item => {
      const result = activeFilters.every(([filterLabel, filterVal]) => {
        const levelKey = labelToLevelKey[filterLabel];
        if (!levelKey) return false;
        const itemValue = item.levels?.[levelKey];
        const selectedValues = Array.isArray(filterVal) ? filterVal : [filterVal];
        const passes = selectedValues.includes(itemValue);

        return passes;
      });
    return result;
  });

    return filtered.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR'));
  }, [historicalMatrixData, historicalFilters, labelToLevelKey, detailedMatrixSeason]);

  const indicatorData = useMemo(() => {
    let all = historicalMatrixData?.aggregationMetrics ?? [];

    if (!selectedType) return [];

    if (selectedType.clusterId) {
      return all
        .filter(row => Number(row.clusterId) === Number(selectedType.clusterId))
        .sort((a, b) => b.seasonId - a.seasonId);
    }

    if (detailedMatrixSeason) all = all.filter(row => row.seasonId === detailedMatrixSeason);

    return all
      .filter(row =>
        row.levels?.level1 === selectedType.levels?.level1 &&
        row.levels?.level2 === selectedType.levels?.level2 &&
        row.levels?.level3 === selectedType.levels?.level3 &&
        row.levels?.level4 === selectedType.levels?.level4 &&
        (selectedType.levels?.level5 == null || row.levels?.level5 === selectedType.levels?.level5) &&
        (selectedType.levels?.level6 == null || row.levels?.level6 === selectedType.levels?.level6) &&
        (selectedType.levels?.level7 == null || row.levels?.level7 === selectedType.levels?.level7)
      )
      .sort((a, b) => b.period - a.period);
  }, [selectedType, historicalMatrixData, detailedMatrixSeason]);

  const detailedMatrixOption = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const seen = new Map();
    historicalMatrixData?.aggregationMetrics
      ?.filter(s => s.year === currentYear)
      .forEach(s => {
        if (!seen.has(s.seasonId)) seen.set(s.seasonId, s.seasonName);
      });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [historicalMatrixData]);
  
  useEffect(() => {
    if (detailedMatrixOption?.length > 0) setDetailedMatrixSeason(detailedMatrixOption[0].value);
  }, [detailedMatrixOption]);

  const handleSetSelectedType = useCallback((val) => {
    setSelectedType(val);
    setUserSelectedTypeId(val?.attributePerformanceMatrixAggregationId ?? null);
  }, []);

  useEffect(() => {
    if (activeTab !== 'historical') return;
    if (!historicalMatrixData) return;
    if (selectedType) return;
    if (!filteredTypes.length) return;

    const best = [...filteredTypes].sort((a, b) => b.score - a.score)[0];
    handleSetSelectedType(best);
  }, [activeTab, historicalMatrixData, filteredTypes, selectedType, handleSetSelectedType]);

  const handleSetDetailedMatrixSeason = useCallback((season) => {
    setDetailedMatrixSeason(season);
    setSelectedType(null);
    setUserSelectedTypeId(null);
  }, []);

  /** 
   * Lógica para aba de estimativas de performance
   */
  const estimatesSeasons = useMemo(() => {
    const productsCurrentOnDisplay = typesFromPerformanceAggregationMatrix ?? [];
    const seen = new Set();
    return productsCurrentOnDisplay
      .filter(item => {
        if (seen.has(item.seasonId)) return false;
        seen.add(item.seasonId);
        return true;
      })
      .sort((a, b) => b.seasonId - a.seasonId)
      .map(item => ({ value: item.seasonId, label: item.seasonName }));
  }, [typesFromPerformanceAggregationMatrix]);

  const estimatesSeason = estimatesSeasons[0]?.value ?? null;

  const filterOptionsPerformancePrediction = useMemo(() => buildFirstStepOptionsByView({
    levels: typesFromPerformanceAggregationMatrix || [],
    labels: hierarchy?.labels ?? {},
    activeFilters: Object.entries(estimatesFilters).filter(([, val]) => val && (Array.isArray(val) ? val.length > 0 : true)),
    seasonId: estimatesSeason,
    labelToLevelKey,
  }), [hierarchy, estimatesFilters, estimatesSeason, labelToLevelKey, typesFromPerformanceAggregationMatrix]);

  const productIndicatorData = useMemo(() => {
    const all = collectionPredictionData?.indicatorData ?? [];
    if (!selectedProduct) return [];

    const productRows = all
      .filter(row => row.typeName === selectedProduct.name)
      .map(row => ({
        ...row,
        level8: selectedProduct.levels?.level8 ?? null,
        estimatedScore: row.seasonId === selectedProduct.seasonId ? selectedProduct.score : null,
      }));

    const similarRows = (selectedProduct?.similarity ?? []).map(similar => ({
      typeName: similar.level4,
      seasonId: similar.seasonId,
      seasonName: similar.seasonName,
      year: similar.year,
      purchaseAffinity: similar.purchaseAffinity,
      assortmentShare: similar.assortmentShare,
      profitShare: similar.profitShare,
      quantitySalesShare: similar.quantitySalesShare,
      revenueShare: similar.revenueShare,
      pastScore: similar.pastScore,
      salesOccupation: null,
      isSimilar: similar.seasonId === selectedProduct.seasonId,
      level8: similar.level8,
    }));

    return [...productRows, ...similarRows]
      .sort((a, b) => (b.seasonId ?? 0) - (a.seasonId ?? 0));
  }, [selectedProduct, collectionPredictionData]);

  const filteredProducts = useMemo(() => {
    let productsCurrentOnDisplay = typesFromPerformanceAggregationMatrix ?? [];

    if (estimatesSeason) productsCurrentOnDisplay = productsCurrentOnDisplay.filter(item => item.seasonId === estimatesSeason);

    const activeFilters = Object.entries(estimatesFilters).filter(([, val]) => {
      if (!val) return false;
      if (Array.isArray(val)) return val.length > 0;
      return true;
    });

    if (activeFilters.length === 0) return productsCurrentOnDisplay;

    return productsCurrentOnDisplay.filter(type =>
      activeFilters.every(([filterLabel, filterVal]) => {
        const levelKey = labelToLevelKey[filterLabel];
        if (!levelKey) return true;
        const typeVal = type.levels?.[levelKey];
        const vals = Array.isArray(filterVal) ? filterVal : [filterVal];
        return vals.includes(typeVal);
      })
    );
  }, [estimatesFilters, labelToLevelKey, estimatesSeason, typesFromPerformanceAggregationMatrix]);

  const handleSetSelectedProduct = useCallback((val) => {
    setSelectedProduct(val);
    setUserSelectedProductId(val?.attributePerformanceMatrixId ?? null);
  }, []);  
  
  useEffect(() => {
    if (activeTab !== ASSORTMENT_INDUSTRY_TABS.PERFORMANCE_PREDICTION) return;
    if (!collectionPredictionData) return;
    if (selectedProduct) return;
    if (!filteredProducts.length) return;

    const best = [...filteredProducts].sort((a, b) => b.score - a.score)[0];
    handleSetSelectedProduct(best);
  }, [activeTab, collectionPredictionData, filteredProducts, selectedProduct, handleSetSelectedProduct]);  

  // performance de produto (análises históricas)
  const ppAllProducts = useMemo(() => {
    return productPerformanceSummaryData?.productReport ?? [];
  }, [productPerformanceSummaryData]);

  const ppColumns = useMemo(() => [
    {
      id: 'expand',
      size: 32,
      enableSorting: false,
      header: () => null,
      cell: () => null,
    },
    {
      id: 'icon',
      size: 40,
      enableSorting: false,
      header: () => null,
      cell: () => null,
    },
    {
      id: 'produto',
      accessorFn: row => row.name,
      header: 'PRODUTO',
      enableSorting: true,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>
              {item.levels?.level8 ?? ''}
              {item.levels?.level8 && <span style={{ color: '#aaa', fontWeight: '400' }}> — {item.name}</span>}
              {!item.levels?.level8 && item.name}
            </div>
            <div style={{ fontSize: '11px', color: '#aaa', marginTop: 1 }}>{item.path}</div>
          </div>
        );
      },
    },
    {
      id: 'grade',
      accessorFn: row => row.levels?.level3,
      header: 'GRADE',
      enableSorting: true,
      cell: ({ row }) => {
        const item = row.original?.levels?.level3;
        return (
          <div>
            <div style={{ fontSize: '11px', color: '#aaa', marginTop: 1 }}>{item}</div>
          </div>
        );
      },
    },
    {
      id: 'hierarquia',
      accessorFn: row => row.levels,
      header: () => hierarchy?.labels?.level2.toUpperCase(),
      enableSorting: true,
      cell: ({ row }) => {
        const { level2 } = row.original?.levels;
      
        return (
          <div>
            <div style={{ fontSize: '11px', color: '#aaa', marginTop: 1 }}>{level2}</div>
          </div>
        );
      },
    },        
    {
      id: 'marca',
      accessorFn: row => row.levels?.level1,
      header: 'MARCA',
      enableSorting: true,
      cell: ({ getValue }) => (
        <span style={{
          display: 'inline-block', padding: '4px 12px', borderRadius: '20px',
          border: '1px solid #efefef', fontSize: '12px', color: '#555', fontWeight: '500',
          backgroundColor: '#fafafa', letterSpacing: '0.01em',
        }}>
          {getValue() ?? '—'}
        </span>
      ),
    },
    {
      id: 'purchaseAffinity',
      accessorFn: row => row.buyers?.shares?.buyingCustomersShare,
      header: 'CLIENTES QUE COMPRAM',
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue();
        const color = ppGetPctColor(val);
        return (
          <span style={{ fontSize: '13px', fontWeight: '600', color: val != null ? color : '#aaa' }}>
            {ppFmtPct(val)}
          </span>
        );
      },
    },
    {
      id: 'profitShare',
      accessorFn: row => row.financialData?.profitShare,
      header: 'PART. LUCRO',
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue();
        const color = ppGetPctColor(val);
        return (
          <span style={{ fontSize: '13px', fontWeight: '600', color: val != null ? color : '#aaa' }}>
            {ppFmtPct(val)}
          </span>
        );
      },
    },
    {
      id: 'unitsSold',
      accessorFn: row => row.financialData?.unitsSold,
      header: 'VENDA (PÇS)',
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue();
        return (
          <span style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: '700' }}>
            {val != null ? val.toLocaleString('pt-BR') : '—'}
          </span>
        );
      },
    },
    {
      id: 'revenue',
      accessorFn: row => row.financialData?.revenue,
      header: 'VENDA ($)',
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue();
        return (
          <span style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: '700' }}>
            {val != null ? currencyFormatter.format(val) : '—'}
          </span>
        );
      },
    },
    {
      id: 'score',
      accessorFn: row => row.score,
      header: 'NOTA',
      enableSorting: true,
      cell: ({ getValue }) => {
        const score = getValue();
        const color = score >= 8 ? '#2ea043' : score >= 5 ? '#d97706' : '#dc2626';
        const bg = score >= 8 ? 'rgba(46,160,67,0.08)' : score >= 5 ? 'rgba(217,119,6,0.08)' : 'rgba(220,38,38,0.08)';
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 52, padding: '4px 10px', borderRadius: '20px',
            backgroundColor: bg, color, fontWeight: '700', fontSize: '13px',
          }}>
            {score.toFixed(2)}
          </span>
        );
      },
    },
  ], [hierarchy]);

  const ppFilterOptions = useMemo(() => buildFirstStepOptionsByView({
    levels: typesFromProductReport || [],
    labels: hierarchy?.labels ?? {},
    activeFilters: Object.entries(ppAdvancedFilters).filter(([_, v]) => v && (Array.isArray(v) ? v.length > 0 : true)),
    seasonId: productReportSeason,
    labelToLevelKey,
    lowerLevelsHierarchy: ['level5', 'level6', 'level7'],
  }), [typesFromProductReport, hierarchy, ppAdvancedFilters, productReportSeason, labelToLevelKey]);

  const ppProducts = useMemo(() => {
    let products = productPerformanceSummaryData?.productReport?.filter(item => item?.season?.seasonId === productReportSeason);

    const searchQuery = ppCodeFilter.trim().toLowerCase();
    if (searchQuery) {
      products = products?.filter(item =>
        (item.levels?.level8 ?? '').toLowerCase().includes(searchQuery) ||
        (item.name ?? '').toLowerCase().includes(searchQuery)
      );
    }

    const activePpFilters = Object.entries(ppAdvancedFilters).filter(
      ([, value]) => value && (Array.isArray(value) ? value.length > 0 : true)
    );

    if (activePpFilters.length > 0) {
      products = products.filter(item =>
        activePpFilters.every(([filterLabel, filterValue]) => {
          const levelKey = labelToLevelKey[filterLabel];
          if (!levelKey) return true;
          const itemValue = item.levels?.[levelKey];
          const selectedValues = Array.isArray(filterValue) ? filterValue : [filterValue];
          return selectedValues.includes(itemValue);
        })
      );
    }

    return products;
  }, [productPerformanceSummaryData, productReportSeason, ppCodeFilter, ppAdvancedFilters, labelToLevelKey]);
 
  const productReportSeasons = useMemo(() => {
  const seasonsMap = new Map();

  (productPerformanceSummaryData?.productReport ?? []).forEach(({ season }) => {
    if (season?.seasonId == null || seasonsMap.has(season.seasonId)) return;
    seasonsMap.set(season.seasonId, {
      value: season.seasonId,
      label: season.seasonName,
      isCurrentSellingSeason: season.isCurrentSellingSeason,
    });
  });

  return Array.from(seasonsMap.values()).sort((a, b) => b.value - a.value);
}, [productPerformanceSummaryData]);

  useEffect(() => {
    if (!productReportSeasons.length) return;

    const newestCurrentSelling = [...productReportSeasons]
      .sort((a, b) => b.value - a.value)
      .find((season) => season.isCurrentSellingSeason);

    setProductReportSeason(newestCurrentSelling?.value ?? productReportSeasons[0]?.value ?? null);
  }, [productReportSeasons]);
  
  // handlers
  const handleSetPpCodeFilter = useCallback((val) => setPpCodeFilter(val), []);
  const handleSetPpAdvancedFilters = useCallback((updater) => setPpAdvancedFilters(updater), []);
  const handleClearPpAdvancedFilters = useCallback(() => setPpAdvancedFilters({}), []);
  const handleSetPdfExportProductIds = useCallback((ids) => setPdfExportProductIds(ids), []);
  const handleSetPdfExportSeasonId = useCallback((id) => setPdfExportSeasonId(id), []);
  
  // troca de abas
  useEffect(() => {
    setOnlyWithSimilars(false);
  }, [activeTab])

  // aba de histórico (análise histórica)
  const handleSetHistoricalFilters = useCallback((val) => {
    setHistoricalFilters(val);
    setSelectedType(null);
  }, []);

  const handleClearHistoricalFilters = useCallback(() => {
    setHistoricalFilters({});
    setSelectedType(null);
    setUserSelectedTypeId(null);
  }, []);

  // aba de estimativas (análise histórica)
  const handleSetEstimatesFilters = useCallback((val) => {
    setEstimatesFilters(val);
    setSelectedProduct(null);
  }, []);

  const handleClearEstimatesFilters = useCallback(() => {
    setEstimatesFilters({});
    setSelectedProduct(null);
    setUserSelectedProductId(null);
  }, []);

  const handleSetOnlyWithSimilars = useCallback((val) => setOnlyWithSimilars(val), []);

  // aba de dados financeiros (análises históricas)
  const handleSetSelectedBrands = useCallback((val) => {
    setSelectedBrands(val);
  }, []);

  const handleSetFinancialSeason = useCallback((val) => {
    setFinancialSeason(val);
  }, []);

  return {
    actions: {
      // setActiveTab: handleSetActiveTab,
      setHistoricalFilters: handleSetHistoricalFilters,
      clearHistoricalFilters: handleClearHistoricalFilters,

      setSelectedType: handleSetSelectedType,
      setEstimatesFilters: handleSetEstimatesFilters,
      clearEstimatesFilters: handleClearEstimatesFilters,

      setSelectedProduct: handleSetSelectedProduct,
      setOnlyWithSimilars: handleSetOnlyWithSimilars,

      setSelectedBrands: handleSetSelectedBrands,

      setFinancialSeason: handleSetFinancialSeason,

      setPpCodeFilter: handleSetPpCodeFilter,
      setPpAdvancedFilters: handleSetPpAdvancedFilters,
      clearPpAdvancedFilters: handleClearPpAdvancedFilters,

      setDetailedMatrixSeason: handleSetDetailedMatrixSeason,

      // performance de produto
      setPpExpandedProductId: useCallback((id) => setPpExpandedProductId(id), []),
      setProductReportSeason: setProductReportSeason,
      setPdfExportProductIds: handleSetPdfExportProductIds,
      setPdfExportSeasonId: handleSetPdfExportSeasonId,
    },
    ctx: {
      // aba de finanças
      columns: financialTabColumns,
      tableData: visibleBrandsWithShares,
      financialBrands: financialBrands,
      visibleBrands: visibleBrands,
      financialTotals: financialTotals,
      financialSeasonOptions: financialSeasonOptions,
      
      // aba histórica
      filterOptions: filterOptions,
      types: filteredTypes,
      indicatorData: indicatorData,
      detailedMatrixSeasonsOptions: detailedMatrixOption,

      // aba de performance futura
      productImageData: productImageData,

      // aba de estimativas
      products: filteredProducts,
      productIndicatorData: productIndicatorData,
      estimatesSeasonOptions: estimatesSeasons,
      performancePredictionOptions: filterOptionsPerformancePrediction,

      // performance de produto
      ppColumns: ppColumns,
      ppFilterOptions: ppFilterOptions,
      ppProducts: ppProducts,
      ppExpandedProductId: ppExpandedProductId,
      ppSeasonOptions: productReportSeasons,
      productTagData: productTagData,
      pdfExportImages: pdfExportImagesData,
      ppAllProducts: ppAllProducts
    },
    uiState: {
      // carregamento total
      isLoadingHistoricalMatrixData: isLoadingHistoricalMatrixData,
      isLoadingFinancialSummaryData: isLoadingFinancialSummaryData,
      isLoadingProductPerformanceSummaryData: isLoadingProductPerformanceSummaryData,
      isLoadingCollectionPredictionData: isLoadingCollectionPredictionData,

      // histórico
      historicalFilters,
      detailedMatrixSeason: detailedMatrixSeason,

      // estimativas
      isLoadingProductImage: isLoadingProductImage,
      selectedType: selectedType,
      estimatesFilters: estimatesFilters,
      selectedProduct: selectedProduct,
      estimatesSeason: estimatesSeason,
      onlyWithSimilars: onlyWithSimilars,
      
      // finanças
      selectedBrands: selectedBrands,
      financialSeason: financialSeason,
      
      userSelectedTypeId: userSelectedTypeId,
      userSelectedProductId: userSelectedProductId,

      // performance por produto
      ppCodeFilter: ppCodeFilter,
      ppAdvancedFilters: ppAdvancedFilters,
      productReportSeason: productReportSeason,
      isLoadingProductTag: isLoadingProductTag,
      isLoadingPdfExportImages: isLoadingPdfExportImages
    },
  };
};  

